/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useEffect, useRef } from 'react';
import { dateUtils } from '@opengeoweb/shared';
import type { Dimension, RefTimeDimensionModes } from '@opengeoweb/webmap';
import { useGetWMLayerInstance } from './useHooksGetCapabilities';
import { useSetIntervalWhenVisible } from './useSetInterValWhenVisible';
import type { OpenLayersLayerProps } from '../components/OpenLayers/layers';
import type { TimeContextType } from '../components/OpenLayers/types/Timespan';
import { makeTimeList } from '../components/OpenLayers/utils/makeDimValuesToPrefetch';

interface UseAnimationForLayerProps {
  dimensions: Dimension[];
  timeContext: TimeContextType;
}

const USEANIMATIONFORLAYER_DEFAULT_NUMSTEPS = 48;
const USEANIMATIONFORLAYER_DEFAULT_UPDATEINTERVALMS = 60000;
const USEANIMATIONFORLAYER_DEFAULT_MSINHOUR = 3600000;

export const useAnimationForLayer = (
  layerProps: OpenLayersLayerProps,
  intervalInMs: number,
  numStepsTillLatest = USEANIMATIONFORLAYER_DEFAULT_NUMSTEPS,
  checkForUpdatesMs = USEANIMATIONFORLAYER_DEFAULT_UPDATEINTERVALMS,
  refTimeDimensionModes?: RefTimeDimensionModes,
): UseAnimationForLayerProps => {
  const now = dateUtils.getNowUtc().getTime();
  const animationStep = useRef<number>(0);
  const [timeValue, setTimeValue] = React.useState<string>('');
  const start = now - USEANIMATIONFORLAYER_DEFAULT_MSINHOUR;
  const end = now;
  const step = USEANIMATIONFORLAYER_DEFAULT_MSINHOUR;
  const [timeSpan, setTimeSpan] = React.useState<TimeContextType>({
    isAnimating: true,
    prefetchList: makeTimeList({ start, end, step }),
  });

  const { wmLayer } = useGetWMLayerInstance(layerProps);

  const trigger = React.useCallback((): void => {
    animationStep.current += 1;
    if (animationStep.current >= numStepsTillLatest) {
      animationStep.current = 0;
    }
    if (wmLayer) {
      const timeDim = wmLayer.getDimension('time');
      if (timeDim) {
        const numTimeSteps = timeDim.size();
        const timeString = timeDim.getValueForIndex(
          animationStep.current + numTimeSteps - numStepsTillLatest,
        ) as string;
        setTimeValue(timeString);
      }
    }
  }, [numStepsTillLatest, wmLayer]);

  useSetIntervalWhenVisible(trigger, intervalInMs);

  const calculateTimeSpan = React.useCallback(() => {
    const timeDim = wmLayer?.getDimension('time');
    if (!timeDim || timeDim.size() < 2) {
      return;
    }
    const animStartTimeStep = Math.max(0, timeDim.size() - numStepsTillLatest);
    const lastTimeStep = Math.max(0, timeDim.size() - 1);
    // There are no timesteps available
    if (lastTimeStep <= animStartTimeStep) {
      return;
    }
    const secondTolastTimeStep = Math.max(0, timeDim.size() - 2);
    const timeResolution =
      timeDim.getValueForIndexAsTime(lastTimeStep) -
      timeDim.getValueForIndexAsTime(secondTolastTimeStep);
    const newTimeSpan: TimeContextType = {
      isAnimating: true,
      prefetchList: makeTimeList({
        start: timeDim.getValueForIndexAsTime(animStartTimeStep),
        end: timeDim.getValueForIndexAsTime(lastTimeStep),
        step: timeResolution,
      }),
    };

    if (timeValue === '') {
      setTimeValue(timeDim.getValueForIndex(animStartTimeStep) as string);
    }

    setTimeSpan(newTimeSpan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numStepsTillLatest, wmLayer]);

  useEffect(() => {
    calculateTimeSpan();
  }, [calculateTimeSpan, wmLayer]);

  const refetchLayer = React.useCallback(async () => {
    await wmLayer?.parseLayer(true);
    calculateTimeSpan();
  }, [calculateTimeSpan, wmLayer]);

  useSetIntervalWhenVisible(refetchLayer, checkForUpdatesMs);

  return {
    dimensions: [{ name: 'time', currentValue: timeValue }],
    timeContext: { ...timeSpan, refTimeDimensionModes },
  };
};
